import React, { useMemo, useState } from 'react';
import { previewDocumentId } from './DocumentIdFormatBuilder.js';
import { LR_SHIPMENT_ID_FORMAT } from '../../utils/shipmentIdFormat.js';

const fillPreview = (fmt, seq, senderCode) => {
  const withSeq = String(fmt || '')
    .replace(/\{seq\}/gi, String(seq))
    .replace(/\{sender\}/gi, senderCode || 'HUB');
  return previewDocumentId(withSeq);
};

export default function HubSequenceSettings({
  mode,
  onModeChange,
  starts,
  onStartChange,
  shipmentFormat,
  mlFormat,
  resetsMonthly,
  onResetsMonthlyChange,
}) {
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();
  const rows = useMemo(() => {
    const list = Array.isArray(starts) ? starts : [];
    if (!query) return list;
    return list.filter((row) => {
      const name = String(row.location || '').toLowerCase();
      const code = String(row.location_code || '').toLowerCase();
      return name.includes(query) || code.includes(query);
    });
  }, [starts, query]);

  const shipmentHasLocation = String(shipmentFormat || '').toLowerCase().includes('{sender}')
    || shipmentFormat === LR_SHIPMENT_ID_FORMAT;
  const mlHasLocation = String(mlFormat || '').toLowerCase().includes('{sender}');
  const perHub = mode === 'per_location' || mode === 'custom';

  return (
    <div className="space-y-4 border-t border-slate-200 pt-4">
      <div>
        <p className="text-xs font-semibold text-gray-700">Where the number counts from</p>
        <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">
          This decides the next shipment number and the next ML sheet number. Numbers already issued stay as they are.
        </p>
      </div>

      <div className="space-y-2">
        {[
          {
            id: 'shared',
            title: 'One sequence for the whole company',
            detail: 'The next number follows the ID pattern. A new month, a new financial year, or a different hub in that pattern starts its own count. The number-sequence style starts again on the 1st of each month.',
          },
          {
            id: 'per_location',
            title: 'Each location starts at 1',
            detail: 'The from location owns one count. Mumbai’s first booking is 1 and Sangli’s first booking is also 1. Mumbai to Goa and Mumbai to Pune share Mumbai’s count. Use Start again each month below when that count should return to 1 on the 1st.',
          },
          {
            id: 'custom',
            title: 'Custom start per location',
            detail: 'Same separate count per from location. Set how far each hub has already counted. 0 means the first number is 1. 150 means the next number is 151. With Start again each month, that start is the floor for every new month.',
          },
        ].map((option) => (
          <label key={option.id} className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="shipment_sequence_mode"
              checked={mode === option.id}
              onChange={() => onModeChange(option.id)}
              className="w-4 h-4 mt-0.5 text-buttonBg focus:ring-blue-400"
            />
            <span>
              <span className="block text-xs sm:text-sm font-medium text-secondary">{option.title}</span>
              <span className="block text-[11px] text-gray-500 leading-relaxed">{option.detail}</span>
            </span>
          </label>
        ))}
      </div>

      {perHub && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-gray-800">Start again each month</p>
            <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">
              When ON, each from hub starts again on the 1st. 0 means the first number of the month is 1.
              A custom start of 20 means the first number of the month is 21. This month keeps the number it has already reached.
              If the shipment ID or the ML sheet ID has no month letter, Save places it in front of the number.
              October’s 1 and November’s 1 then stay different. The number-sequence style already has that letter.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!resetsMonthly}
            onClick={() => onResetsMonthlyChange(!resetsMonthly)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              resetsMonthly ? 'bg-buttonBg' : 'bg-gray-300'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                resetsMonthly ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      )}

      {perHub && resetsMonthly && (() => {
        const shipmentNeedsMonth = !String(shipmentFormat || '').toLowerCase().includes('{mlet}')
          && shipmentFormat !== LR_SHIPMENT_ID_FORMAT;
        const mlNeedsMonth = !String(mlFormat || '').toLowerCase().includes('{mlet}');
        if (!shipmentNeedsMonth && !mlNeedsMonth) return null;
        const missing = [
          shipmentNeedsMonth ? 'shipment ID' : null,
          mlNeedsMonth ? 'ML sheet ID' : null,
        ].filter(Boolean);
        const which = missing.length === 2 ? `${missing[0]} and the ${missing[1]}` : missing[0];
        const verb = missing.length === 2 ? 'have' : 'has';
        return (
          <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
            {`The ${which} ${verb} no month letter, so October’s 1 and November’s 1 would be the same number. Save places the month letter in front of the number.`}
          </p>
        );
      })()}

      {perHub && (!shipmentHasLocation || !mlHasLocation) && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
          {!shipmentHasLocation && 'Add the sender hub ({sender}) to the shipment ID, or use the number-sequence style. '}
          {!mlHasLocation && 'Add the sender hub ({sender}) to the ML sheet ID. '}
          Two locations would otherwise try to print the same number.
        </p>
      )}

      {mode === 'custom' && (
        <div className="space-y-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search location hub or code"
            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <div className="max-h-72 overflow-auto border border-gray-200 rounded-xl">
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-gray-600 sticky top-0">
                <tr>
                  <th className="text-left font-semibold px-3 py-2">Location</th>
                  <th className="text-left font-semibold px-3 py-2">Shipment start</th>
                  <th className="text-left font-semibold px-3 py-2">Next shipment</th>
                  <th className="text-left font-semibold px-3 py-2">ML start</th>
                  <th className="text-left font-semibold px-3 py-2">Next ML</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-gray-500">
                      No location hubs match.
                    </td>
                  </tr>
                )}
                {rows.map((row) => {
                  const shipStart = Math.max(0, parseInt(row.shipment_start, 10) || 0);
                  const mlStart = Math.max(0, parseInt(row.ml_start, 10) || 0);
                  const code = row.location_code || 'HUB';
                  return (
                    <tr key={row.location_hub_id} className="border-t border-gray-100">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-800">{row.location}</div>
                        <div className="font-mono text-[11px] text-gray-500">{row.location_code}</div>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={row.shipment_start ?? 0}
                          onChange={(e) => onStartChange(row.location_hub_id, 'shipment_start', e.target.value)}
                          className="w-24 px-2 py-1 border border-gray-300 rounded-lg"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-700">
                        {shipmentFormat === LR_SHIPMENT_ID_FORMAT
                          ? `serial ${shipStart + 1}`
                          : fillPreview(shipmentFormat, shipStart + 1, code)}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={row.ml_start ?? 0}
                          onChange={(e) => onStartChange(row.location_hub_id, 'ml_start', e.target.value)}
                          className="w-24 px-2 py-1 border border-gray-300 rounded-lg"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-700">
                        {fillPreview(mlFormat, mlStart + 1, code)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-500 leading-relaxed">
            0 means the first booking is 1. 150 means the next booking is 151. Raising the start skips the numbers in between.
            If this hub already passed that number, the next booking continues after the highest number already used.
            With Start again each month, the next month begins at this start again. Numbers already issued stay as they are.
            Shipment start and ML start are separate.
          </p>
        </div>
      )}
    </div>
  );
}
